export const DEFAULT_DISPLAY_TIMEZONE = "Asia/Kolkata";

export function resolveDisplayTimezone(_orgContext = null, explicitTimeZone = null) {
  const candidate = String(explicitTimeZone || DEFAULT_DISPLAY_TIMEZONE).trim();
  try {
    new Intl.DateTimeFormat("en", { timeZone: candidate }).format(new Date(0));
    return candidate;
  } catch (_) {
    return DEFAULT_DISPLAY_TIMEZONE;
  }
}

function partsForInstant(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  return Object.fromEntries(parts.map(({ type, value }) => [type, value]));
}

function zoneOffsetMilliseconds(date, timeZone) {
  const parts = partsForInstant(date, timeZone);
  const representedAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return representedAsUtc - Math.floor(date.getTime() / 1000) * 1000;
}

function wallClockToInstant(values, timeZone) {
  const wallClockUtc = Date.UTC(
    values.year,
    values.month - 1,
    values.day,
    values.hour,
    values.minute,
    values.second,
    values.millisecond,
  );
  let instant = wallClockUtc;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const offset = zoneOffsetMilliseconds(new Date(instant), timeZone);
    const adjusted = wallClockUtc - offset;
    if (adjusted === instant) break;
    instant = adjusted;
  }
  const parsed = new Date(instant);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function naiveDateParts(match) {
  const fractional = String(match[7] || "");
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4] || 0),
    minute: Number(match[5] || 0),
    second: Number(match[6] || 0),
    millisecond: Number(fractional.slice(0, 3).padEnd(3, "0") || 0),
  };
}

export function parseDateLike(
  value,
  { orgContext = null, timeZone: explicitTimeZone = null } = {},
) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  if (!s) return null;
  const timeZone = resolveDisplayTimezone(orgContext, explicitTimeZone);

  const compact = s.match(
    /^(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/,
  );
  if (compact) {
    return wallClockToInstant(naiveDateParts(compact), timeZone);
  }

  const naive = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?$/,
  );
  if (naive) return wallClockToInstant(naiveDateParts(naive), timeZone);

  const instant = new Date(s);
  if (!Number.isNaN(instant.getTime())) return instant;
  return null;
}

export function formatDateTime(
  value,
  { fallback = "—", orgContext = null, timeZone: explicitTimeZone = null } = {},
) {
  const timeZone = resolveDisplayTimezone(orgContext, explicitTimeZone);
  const date = parseDateLike(value, { orgContext, timeZone });
  if (!date) return value ? String(value) : fallback;
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function formatDateTimeCompact(
  value,
  { fallback = "—", orgContext = null, timeZone: explicitTimeZone = null } = {},
) {
  const timeZone = resolveDisplayTimezone(orgContext, explicitTimeZone);
  const date = parseDateLike(value, { orgContext, timeZone });
  if (!date) return value ? String(value) : fallback;
  const parts = partsForInstant(date, timeZone);
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}

export function formatDateTimeFull(
  value,
  { fallback = "—", orgContext = null, timeZone: explicitTimeZone = null } = {},
) {
  const timeZone = resolveDisplayTimezone(orgContext, explicitTimeZone);
  const date = parseDateLike(value, { orgContext, timeZone });
  if (!date) return value ? String(value) : fallback;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
      .formatToParts(date)
      .map(({ type, value: partValue }) => [type, partValue]),
  );
  return `${parts.day}-${parts.month}-${parts.year}, ${parts.hour}:${parts.minute}:${parts.second} ${parts.dayPeriod}`;
}

export function formatDateTimeForExport(
  value,
  { fallback = "", orgContext = null, timeZone: explicitTimeZone = null } = {},
) {
  const timeZone = resolveDisplayTimezone(orgContext, explicitTimeZone);
  const date = parseDateLike(value, { orgContext, timeZone });
  if (!date) return value ? String(value) : fallback;
  const parts = partsForInstant(date, timeZone);
  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
}

export function formatConfidence(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n * 100)}%`;
}
